﻿import { createPinia, defineStore } from 'pinia'
import { createPersistedState } from 'pinia-plugin-persistedstate'
import { url$ } from '../enums'
import { utility, utilfcty$ } from '../utils/utility'
import { getCurrentInstance, Ref } from 'vue'
import { i18n } from '@/locales';

export const piniaStore = (app: any) => {
    const pinia = createPinia()
    const piniaPersist = createPersistedState();
    pinia.use(piniaPersist)
    app.use(pinia)
}

/**
 * root store
 **/
export const useApp = defineStore('App', {
    persist: {
        pick: ['styling']
    },
    state: () => {
        const ready: boolean = false;
        const logger: string = '';
        const rootState = {
            ready,
            //用於紀錄目前log所在的位置
            logger,
            //全局忙碌控制
            appBusy: false,
            //全局忙碌時顯示字串
            busyWords: '',
            //用來重整畫面的序號值
            refreshKey: 0,
            //顯示主題樣式
            visibleThemeStyle: false,
            //顯示帳號管理
            visibleAccMgmt: false,
            //系統參數
            parameters: {
                //以下參數Browser自訂
            } as any,
            styling: {
                $appState: {} as any,
                $primevue: {} as any,
                fontSize: '12',
                inputStyle: 'outlined',
                ripple: true,
                theme: 'light',
            }
        };
        return rootState;
    },
    getters: {
        state: (state: any) => (key: string) => { return state[key]; },
        util$: (state: any) => {
            return new utility(utilfcty$.util$.vueRouter$, utilfcty$.util$.log$.vueLogger$, utilfcty$.util$.toast$, utilfcty$.util$.confirm$, state.logger)
        },
        vueUse: (state) => (logger: string = '') => utilfcty$.util$.vueUse(logger ? logger : state.logger)
    },
    actions: {
        /**
         * 系統初始(登入前)
         */
        async initialize() {
            const util$ = useApp().util$;

            //進入initializeApp
            util$.log$.warn('Initialize app ...');

            //載入styling參數
            const instance = getCurrentInstance();
            this.$patch((state) => {
                //primevue $appState
                state.styling.$appState = instance?.appContext.app.config.globalProperties.$appState;
                //primevue $primevue
                state.styling.$primevue = instance?.appContext.app.config.globalProperties.$primevue;
            })
            //套用UI設定
            this.applyStyling();

            //將Client參數存入
            const response = await util$.http$.get(url$.app + 'bootstrap?env=' + import.meta.env.MODE);
            this.$patch((state) => {
                Object.assign(state.parameters, response);
            });
            this.ready = true;
        },
        /**
         * 套用頁面樣式
         */
        applyStyling() {
            const util$ = useApp().util$;
            //語系
            if (i18n.global.locale.value == "zh-TW") {
                document.documentElement.lang = "zh-hant";
            } else {
                document.documentElement.lang = "en";
            }
            //字型
            document.documentElement.style.fontSize = this.styling.fontSize + 'px';
            util$.log$.info('font size', document.documentElement.style.fontSize);
            //輸入框
            this.styling.$primevue.config.inputStyle = this.styling.inputStyle;
            util$.log$.info('input style', this.styling.$primevue.config.inputStyle);
            //波紋效果
            this.styling.$primevue.config.ripple = this.styling.ripple;
            util$.log$.info('ripple', this.styling.$primevue.config.ripple);
            //theme: light/dark
            const element = document.querySelector('html');
            if (element) {
                if (this.styling.theme == 'dark') {
                    element.classList.add('mpp-app-dark');
                    util$.log$.info('主題黑');
                } else {
                    element.classList.remove('mpp-app-dark');
                    util$.log$.info('主題白');
                }
            }
        },
        /** * 執行需要獨佔視窗的程式碼 
         * @param busyWords 遮罩文字
         * @param action 非同步函式
         */
        async busyFor(busyWords: string, action: (...args: any[]) => Promise<any>, ...args: any[]): Promise<any> {
            // 🛡️ 核心業務邏輯：如果已經在忙碌中，直接攔截，防止狀態被重置或重複執行
            if (this.appBusy) return;

            try {
                this.appBusy = true;
                this.busyWords = busyWords;

                // 這裡必須 await，確保程式碼「停」在這裡等 action 跑完
                return await action(...args);
            } catch (error) {
                // 錯誤往外丟給 UI 或攔截器，但我們已經準備好在 finally 關門了
                throw error;
            } finally {
                // 不論成功 (try) 或失敗 (catch)，一定會跑這裡
                this.busyWords = '';
                this.appBusy = false;
            }
        },

        /**
         * 執行載入程式碼控制旗標
         * @param loading 傳入的 Ref 旗標
         * @param action 非同步函式
         */
        async loadFor(loading: Ref<boolean>, action: (...args: any[]) => Promise<any>, ...args: any[]): Promise<any> {
            // 🛡️ 核心業務邏輯：如果正在載入中，直接攔截，避免重複觸發導致旗標錯亂
            if (loading.value) return;

            try {
                loading.value = true;
                // 這裡直接 await，確保程式碼順序執行
                return await action(...args);
            } catch (error) {
                // 錯誤往外丟，交由攔截器或 UI 層處理
                throw error;
            } finally {
                // 無論成功或失敗，最後一定會執行這一行
                loading.value = false;
            }
        },
        /** 重整頁面 */
        refreshPage() {
            this.$patch((state) => state.refreshKey = state.refreshKey + 1);
        }

    }
})